import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { catalogAPI } from "../services/api";
import { categoryLabel } from "../constants/categories";
import { productImageUrl } from "../pos/utils/images";
import { useI18n } from "../i18n";

const PAGE = 40;

/** The product's name in the shop's language, falling back to the original. */
const nameFor = (item, lang) =>
  (lang === "fr" && item.name_fr) || (lang === "ar" && item.name_ar) || item.name;

/**
 * Starter products: browse the operator's approved catalogue, tick what the
 * shop sells, optionally type its own prices, and copy them in.
 *
 * It is a full-screen sheet so it works with a thumb on a phone: search and
 * category chips on top, the list scrolling, the count and the Add button
 * pinned to the bottom. Used from the Products screen; the onboarding wizard
 * mounts the same component with `skipLabel` set, which turns Close into a
 * prominent Skip.
 *
 * Nothing is added until the owner presses the button, and an item the shop
 * already holds is shown as added and cannot be ticked.
 */
const CatalogPicker = ({ skipLabel = false, onClose, onDone }) => {
  const { t, lang } = useI18n();
  const [templates, setTemplates] = useState(null);
  const [templateId, setTemplateId] = useState(null);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [data, setData] = useState({ items: [], total: 0, categories: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // id -> true. Survives paging, searching and changing category
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [requesting, setRequesting] = useState(false);

  // Debounce the search box
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    catalogAPI
      .templates()
      .then(({ data: list }) => {
        setTemplates(list);
        if (list.length > 0) setTemplateId(list[0].id);
      })
      .catch(() => {
        setTemplates([]);
        setError(t("catalog.loadFailed"));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(
    async (offset) => {
      if (!templateId) return;
      setLoading(true);
      setError("");

      try {
        const { data: page } = await catalogAPI.items(templateId, {
          search: query || undefined,
          category: category || undefined,
          limit: PAGE,
          offset,
        });

        setData((current) => ({
          ...page,
          items: offset === 0 ? page.items : [...current.items, ...page.items],
        }));
      } catch {
        setError(t("catalog.loadFailed"));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templateId, query, category],
  );

  useEffect(() => {
    load(0);
  }, [load]);

  const selectable = data.items.filter((item) => !item.already_copied);
  const pickedIds = Object.keys(picked);
  const allShownPicked = selectable.length > 0 && selectable.every((item) => picked[item.id]);

  const toggle = (item) =>
    setPicked((current) => {
      const next = { ...current };
      if (next[item.id]) delete next[item.id];
      else next[item.id] = true;
      return next;
    });

  const toggleAll = () =>
    setPicked((current) => {
      const next = { ...current };
      if (allShownPicked) selectable.forEach((item) => delete next[item.id]);
      else selectable.forEach((item) => (next[item.id] = true));
      return next;
    });

  const submit = async (wholeCatalogue = false) => {
    setBusy(true);
    setError("");

    try {
      const { data: outcome } = await catalogAPI.copy(
        wholeCatalogue ? { templateId } : { itemIds: pickedIds },
      );
      setResult(outcome);
      setPicked({});
      onDone?.(outcome);
    } catch (err) {
      const body = err.response?.data;
      setError(
        body?.code === "PRODUCT_LIMIT_REACHED"
          ? t("catalog.limitReached", { limit: body.details?.product_limit })
          : body?.message || t("catalog.copyFailed"),
      );
    } finally {
      setBusy(false);
    }
  };

  const addWholeCatalogue = () => {
    const left = current.item_count - current.copied_count;
    if (window.confirm(t("catalog.addAllConfirm", { count: left, name: current.name }))) {
      submit(true);
    }
  };

  const hasMore = data.items.length < data.total;
  const noCatalogue = templates && templates.length === 0 && !error;
  const current = templates?.find((template) => template.id === templateId);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("catalog.title")}
      className="fixed inset-0 z-[60] flex flex-col bg-white"
    >
      <header className="flex items-start justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-gray-800">{t("catalog.title")}</h2>
          <p className="text-sm text-gray-500">{t("catalog.intro")}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={
            skipLabel && !result
              ? "shrink-0 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700"
              : "shrink-0 text-2xl leading-none text-gray-500"
          }
          aria-label={t("catalog.close")}
        >
          {skipLabel && !result ? t("catalog.skip") : "✕"}
        </button>
      </header>

      {result ? (
        <div className="flex-1 overflow-y-auto p-6 text-center">
          <div className="text-5xl mb-3" aria-hidden="true">
            ✅
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-4">{t("catalog.resultTitle")}</h3>
          <div className="space-y-2 text-gray-700">
            {result.created.length > 0 ? (
              <p>{t("catalog.resultAdded", { count: result.created.length })}</p>
            ) : (
              <p>{t("catalog.resultNone")}</p>
            )}
            {result.skipped.length > 0 && result.created.length > 0 && (
              <p>{t("catalog.resultSkipped", { count: result.skipped.length })}</p>
            )}
            {result.drafts > 0 && <p>{t("catalog.resultDrafts", { count: result.drafts })}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="mt-8 rounded-lg bg-primary-600 px-8 py-3 font-semibold text-white"
          >
            {t("catalog.done")}
          </button>
        </div>
      ) : noCatalogue ? (
        <p className="p-8 text-center text-gray-600">{t("catalog.noCatalogue")}</p>
      ) : (
        <>
          <div className="space-y-3 border-b px-4 py-3">
            {templates && templates.length > 1 && (
              <label className="block text-sm text-gray-600">
                {t("catalog.template")}
                <select
                  value={templateId || ""}
                  onChange={(e) => {
                    setTemplateId(e.target.value);
                    setCategory("");
                  }}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-base text-gray-900"
                >
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} (
                      {t("catalog.templateCount", {
                        copied: template.copied_count,
                        total: template.item_count,
                      })}
                      )
                    </option>
                  ))}
                </select>
              </label>
            )}

            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("catalog.searchPlaceholder")}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 text-base focus:ring-2 focus:ring-primary-500"
            />

            <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
              {[{ category: "", count: data.categories.reduce((sum, c) => sum + c.count, 0) }, ...data.categories].map(
                (chip) => (
                  <button
                    key={chip.category || "all"}
                    type="button"
                    onClick={() => setCategory(chip.category)}
                    aria-pressed={category === chip.category}
                    className={`shrink-0 rounded-full px-3 py-1.5 text-sm font-medium ${
                      category === chip.category
                        ? "bg-primary-600 text-white"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {chip.category ? categoryLabel(chip.category) : t("catalog.allCategories")}{" "}
                    <span className="opacity-70">{chip.count}</span>
                  </button>
                ),
              )}
            </div>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={toggleAll}
                disabled={selectable.length === 0}
                className="font-semibold text-primary-600 disabled:opacity-40"
              >
                {allShownPicked ? t("catalog.clearAll") : t("catalog.selectAll")}
              </button>
              {current && (
                <span className="text-gray-500">
                  {t("catalog.templateCount", { copied: current.copied_count, total: current.item_count })}
                </span>
              )}
            </div>

            {/* A whole range in one tap, for a shop that sells most of it */}
            {current && current.item_count - current.copied_count > 0 && (
              <button
                type="button"
                onClick={addWholeCatalogue}
                disabled={busy}
                className="w-full rounded-lg border border-primary-600 px-3 py-2 text-sm font-semibold text-primary-700 disabled:opacity-50"
              >
                {t("catalog.addAll", { count: current.item_count - current.copied_count })}
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {error && <p className="m-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

            <ul className="divide-y">
              {data.items.map((item) => {
                const isPicked = Boolean(picked[item.id]);
                const image = productImageUrl(item.image_url || item.images?.[0]);

                return (
                  <li
                    key={item.id}
                    className={`flex items-center gap-3 px-4 py-3 ${item.already_copied ? "opacity-60" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={isPicked}
                      disabled={item.already_copied}
                      onChange={() => toggle(item)}
                      aria-label={nameFor(item, lang)}
                      className="h-6 w-6 shrink-0"
                    />
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                      {image && <img src={image} alt="" className="h-full w-full object-cover" loading="lazy" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-gray-800">
                        {nameFor(item, lang)}
                        {item.variant_label ? ` · ${item.variant_label}` : ""}
                      </p>
                      <p className="truncate text-xs text-gray-500">
                        {[categoryLabel(item.category), item.brand].filter(Boolean).join(" · ")}
                      </p>
                    </div>

                    {item.already_copied ? (
                      <span className="shrink-0 rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
                        ✓ {t("catalog.added")}
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>

            {loading && <p className="p-4 text-center text-gray-500">{t("catalog.loading")}</p>}
            {!loading && data.items.length === 0 && !error && (
              <p className="p-8 text-center text-gray-500">{t("catalog.empty")}</p>
            )}
            <div className="px-4 py-3 text-center">
              <button
                type="button"
                onClick={() => setRequesting(true)}
                className="text-sm font-semibold text-primary-600 hover:underline"
              >
                {t("catalog.requestLink")}
              </button>
            </div>

            {!loading && hasMore && (
              <button
                type="button"
                onClick={() => load(data.items.length)}
                className="mx-auto my-4 block rounded-lg border border-gray-300 px-5 py-2 font-medium text-gray-700"
              >
                {t("catalog.loadMore")}
              </button>
            )}
          </div>

          <footer className="space-y-2 border-t bg-white px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
            <p className="text-xs text-amber-800">{t("catalog.draftHint")}</p>
            <div className="flex items-center gap-3">
              <span className="flex-1 text-sm font-medium text-gray-700">
                {t("catalog.selected", { count: pickedIds.length })}
              </span>
              <button
                type="button"
                onClick={() => submit(false)}
                disabled={busy || pickedIds.length === 0}
                className="rounded-lg bg-primary-600 px-6 py-3 font-semibold text-white disabled:opacity-50"
              >
                {busy ? t("catalog.adding") : t("catalog.add", { count: pickedIds.length })}
              </button>
            </div>
          </footer>
        </>
      )}
      {requesting && <RequestForm onClose={() => setRequesting(false)} />}
    </div>,
    document.body,
  );
};

/** "Can't find a product? Tell us": goes to the operator, who fills the gap. */
const RequestForm = ({ onClose }) => {
  const { t } = useI18n();
  const [description, setDescription] = useState("");
  const [brand, setBrand] = useState("");
  const [state, setState] = useState("idle");

  const send = async (event) => {
    event.preventDefault();
    setState("sending");

    try {
      await catalogAPI.requestProduct({ description, brand: brand || undefined });
      setState("sent");
    } catch {
      setState("failed");
    }
  };

  return (
    <div className="absolute inset-0 z-10 flex items-end bg-black/40 sm:items-center sm:justify-center">
      <div className="w-full rounded-t-2xl bg-white p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:max-w-md sm:rounded-2xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h3 className="text-lg font-bold text-gray-800">{t("catalog.requestTitle")}</h3>
          <button type="button" onClick={onClose} aria-label={t("catalog.close")} className="text-2xl leading-none text-gray-500">
            ✕
          </button>
        </div>

        {state === "sent" ? (
          <>
            <p className="rounded-lg bg-green-50 p-3 text-green-800">{t("catalog.requestSent")}</p>
            <button type="button" onClick={onClose} className="mt-4 w-full rounded-lg bg-primary-600 py-3 font-semibold text-white">
              {t("catalog.done")}
            </button>
          </>
        ) : (
          <form onSubmit={send} className="space-y-3">
            <p className="text-sm text-gray-600">{t("catalog.requestHint")}</p>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              required
              minLength={2}
              maxLength={1000}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
            <input
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              placeholder={t("catalog.requestBrand")}
              maxLength={100}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
            {state === "failed" && <p className="text-sm text-red-600">{t("catalog.requestFailed")}</p>}
            <button
              type="submit"
              disabled={state === "sending"}
              className="w-full rounded-lg bg-primary-600 py-3 font-semibold text-white disabled:opacity-50"
            >
              {state === "sending" ? t("catalog.requestSending") : t("catalog.requestSend")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default CatalogPicker;
